import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Edit, AlertTriangle, Plus, FileText, Trash2 } from 'lucide-react';
import VitalsDisplay from '@/components/VitalsDisplay';
import SOAPNoteForm from '@/components/SOAPNoteForm';
import VisitHistory from '@/components/VisitHistory';
import PatientFormDialog from '@/components/PatientFormDialog';
import PatientDocuments from '@/components/PatientDocuments';

const PatientDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [visits, setVisits] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showSOAPForm, setShowSOAPForm] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);

  useEffect(() => {
    fetchPatientData();
  }, [id]);

  const fetchPatientData = async () => {
    try {
      const [patientRes, visitsRes, documentsRes] = await Promise.all([
        axios.get(`${API}/patients/${id}`),
        axios.get(`${API}/visits/patient/${id}`),
        axios.get(`${API}/patients/${id}/documents`)
      ]);
      setPatient(patientRes.data);
      setVisits(visitsRes.data);
      setDocuments(documentsRes.data);
    } catch (error) {
      console.error('Error fetching patient data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePatientUpdated = () => {
    fetchPatientData();
    setShowEditDialog(false);
  };

  const handleVisitCreated = () => {
    fetchPatientData();
    setShowSOAPForm(false);
    setEditingVisit(null);
  };

  const handleEditVisit = (visit) => {
    setEditingVisit(visit);
    setShowSOAPForm(true);
  };

  const handleDeleteVisit = async (visitId) => {
    try {
      await axios.delete(`${API}/visits/${visitId}`);
      // Force page reload to ensure fresh data
      window.location.reload();
    } catch (error) {
      console.error('Error deleting visit:', error);
      alert('Erreur lors de la suppression de la consultation');
    }
  };

  const handleDeletePatient = async () => {
    const confirmed = window.confirm(
      `Êtes-vous sûr de vouloir supprimer définitivement ${patient.identite.prenom} ${patient.identite.nom} ?\n\n` +
      'Cette action supprimera :\n' +
      '- Le dossier patient\n' +
      '- Toutes les consultations\n' +
      '- Tous les documents\n' +
      '- Tous les rendez-vous\n\n' +
      'Cette action est IRRÉVERSIBLE.'
    );

    if (!confirmed) return;

    try {
      await axios.delete(`${API}/patients/${id}`);
      // Redirect to patients list
      navigate('/patients');
    } catch (error) {
      console.error('Error deleting patient:', error);
      alert('Erreur lors de la suppression du patient');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Patient non trouvé</p>
        <Button onClick={() => navigate('/patients')} className="mt-4">
          Retour à la liste
        </Button>
      </div>
    );
  }

  const calculateAge = (dob) => {
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="patient-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/patients')}
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {patient.identite.prenom} {patient.identite.nom}
            </h1>
            <p className="text-gray-600">
              {calculateAge(patient.identite.date_naissance)} ans • {patient.identite.sexe === 'M' ? 'Homme' : 'Femme'}
            </p>
          </div>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={handleDeletePatient}
            className="hover:bg-red-50 hover:text-red-600 hover:border-red-200"
            data-testid="delete-patient-button"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowEditDialog(true)}
            data-testid="edit-patient-button"
          >
            <Edit className="w-4 h-4 mr-2" />
            Modifier
          </Button>
          <Button
            onClick={() => setShowSOAPForm(true)}
            data-testid="new-visit-button"
            style={{
              background: 'linear-gradient(135deg, #0066cc 0%, #4a90e2 100%)',
              color: 'white'
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Consultation
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {patient.allergies && patient.allergies.length > 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg" data-testid="allergies-alert">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-3" />
            <div>
              <p className="font-semibold text-red-800">Allergies connues</p>
              <p className="text-red-700">{patient.allergies.join(', ')}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Patient Info */}
        <div className="lg:col-span-1 space-y-6">
          {/* Demographics */}
          <Card data-testid="demographics-card">
            <CardHeader>
              <CardTitle>Informations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Date de naissance</p>
                <p className="font-medium text-gray-900">{patient.identite.date_naissance}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Téléphone</p>
                <p className="font-medium text-gray-900">{patient.identite.telephone || 'Non renseigné'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Adresse</p>
                <p className="font-medium text-gray-900">{patient.identite.adresse || 'Non renseignée'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Assurance</p>
                <p className="font-medium text-gray-900">{patient.assurance || 'Non renseignée'}</p>
                {patient.numero_assurance && (
                  <p className="text-sm text-gray-600">{patient.numero_assurance}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Antécédents */}
          <Card data-testid="antecedents-card">
            <CardHeader>
              <CardTitle>Antécédents Médicaux</CardTitle>
            </CardHeader>
            <CardContent>
              {patient.antecedents && patient.antecedents.length > 0 ? (
                <ul className="space-y-2">
                  {patient.antecedents.map((ant, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span className="text-gray-700">{ant}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 text-sm">Aucun antécédent renseigné</p>
              )}
            </CardContent>
          </Card>

          {/* Latest Vitals */}
          {patient.vitals_derniers && (
            <Card data-testid="latest-vitals-card">
              <CardHeader>
                <CardTitle>Dernières Constantes</CardTitle>
              </CardHeader>
              <CardContent>
                <VitalsDisplay vitals={patient.vitals_derniers} />
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column - Visits & SOAP */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="visits" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="visits" data-testid="visits-tab">
                <FileText className="w-4 h-4 mr-2" />
                Consultations ({visits.length})
              </TabsTrigger>
              <TabsTrigger value="documents" data-testid="documents-tab">
                Documents ({documents.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="visits" className="mt-6">
              <VisitHistory 
                visits={visits} 
                onEdit={handleEditVisit}
                onDelete={handleDeleteVisit}
              />
            </TabsContent>

            <TabsContent value="documents" className="mt-6">
              <PatientDocuments
                patientId={id}
                documents={documents}
                onDocumentsChange={fetchPatientData}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Dialogs */}
      <PatientFormDialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        onSuccess={handlePatientUpdated}
        patient={patient}
      />

      <SOAPNoteForm
        open={showSOAPForm}
        onClose={() => {
          setShowSOAPForm(false);
          setEditingVisit(null);
        }}
        onSuccess={handleVisitCreated}
        patientId={id}
        user={user}
        visit={editingVisit}
      />
    </div>
  );
};

export default PatientDetail;
